// src/api/photoProcessingApi.js (Updated)
import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const WS_URL = process.env.PHOTO_PROCESSOR_WS_URL || 'ws://localhost:8000';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication functions
export const authAPI = {
  login: async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login/', {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.errors?.non_field_errors?.[0] || 'Login failed');
    }
  },

  register: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register/', userData);

      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.errors || 'Registration failed');
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },

  verifyToken: async () => {
    try {
      const response = await apiClient.get('/auth/verify-token/');
      return response.data;
    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  },

  getProfile: async () => {
    try {
      const response = await apiClient.get('/auth/profile/');
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  const user = localStorage.getItem('user');
  return !!(token && user);
};

// Get current user
export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};


/**
 * Upload images to the server
 * @param {FormData} formData - Form data containing images
 * @returns {Promise<Object>} Response with image URLs
 */
export const uploadImages = async (formData) => {
  try {
    const response = await apiClient.post('/photo_processing/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // A large batch over a slow uplink can take minutes; the default 30s aborts it
      timeout: 15 * 60 * 1000,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading images:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Upload timed out - try fewer images per batch');
    }
    if (error.response?.status === 413) {
      throw new Error('Batch is too large (max 400 MB in total) - try fewer images per batch');
    }
    throw new Error(
      error.response?.data?.error || error.response?.data?.detail || 'Failed to upload images'
    );
  }
};

/**
 * Process images with the pipeline model
 * @param {Object} images - Object with image URLs
 * @param {Object} config - Processing configuration
 * @returns {Promise<Object>} Processing results
 */
export const processImages = async (images, config = {}) => {
  try {
    // Ensure yolo_model_type is properly formatted
    const processedConfig = {
      confidenceThreshold: config.confidenceThreshold || 0.3,
      iouThreshold: config.iouThreshold || 0.5,
      maxDetections: config.maxDetections || 96,
      saveProcessedImage: config.saveProcessedImage !== false,
      ...config
    };

    // If yolo_model_type is a single value, ensure it's properly typed
    if (processedConfig.yolo_model_type !== undefined && !Array.isArray(processedConfig.yolo_model_type)) {
      processedConfig.yolo_model_type = parseInt(processedConfig.yolo_model_type);
    }

    const response = await apiClient.post('/photo_processing/detect/', {
      images,
      config: processedConfig
    });

    return {
      success: true,
      batch_id: response.data.batch_id,
      job_ids: response.data.job_ids,
      model_info: response.data.model_info
    };
  } catch (error) {
    console.error('Error processing images:', error);
    throw new Error(error.response?.data?.error || 'Failed to start image processing');
  }
};

/**
 * Get job status
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job status and basic info
 */
export const getJobStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/`);
    return response.data;
  } catch (error) {
    console.error('Error getting job status:', error);
    throw new Error(error.response?.data?.error || 'Failed to get job status');
  }
};

/**
 * Get detailed job results
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Detailed job results
 */
export const getJobResults = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/results/`);
    return response.data;
  } catch (error) {
    console.error('Error getting job results:', error);
    throw new Error(error.response?.data?.error || 'Failed to get job results');
  }
};

// src/api/photoProcessingApi.js - Add these functions

/**
 * Share a photo analysis job with a group
 * @param {string} jobId - Job ID
 * @param {Object} shareData - { group_id, permission_level }
 * @returns {Promise<Object>} Share info
 */
export const sharePhotoJob = async (jobId, shareData) => {
  try {
    const response = await apiClient.post(`/photo_processing/jobs/${jobId}/share/`, shareData);
    return response.data;
  } catch (error) {
    console.error('Error sharing job:', error);
    throw new Error(error.response?.data?.error || 'Failed to share job');
  }
};

/**
 * Remove group access from photo analysis job
 * @param {string} jobId - Job ID
 * @param {Object} unshareData - { group_id }
 * @returns {Promise<Object>} Response
 */
export const unsharePhotoJob = async (jobId, unshareData) => {
  try {
    const response = await apiClient.post(`/photo_processing/jobs/${jobId}/unshare/`, unshareData);
    return response.data;
  } catch (error) {
    console.error('Error removing share:', error);
    throw new Error(error.response?.data?.error || 'Failed to remove group access');
  }
};

/**
 * Get photo job sharing information
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Share info
 */
export const getPhotoJobShareInfo = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/share_info/`);
    return response.data;
  } catch (error) {
    console.error('Error getting share info:', error);
    throw new Error(error.response?.data?.error || 'Failed to get sharing information');
  }
};

/**
 * Get grid structure for a job
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Grid structure
 */
export const getJobGrid = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/grid/`);
    return response.data;
  } catch (error) {
    console.error('Error getting job grid:', error);
    throw new Error(error.response?.data?.error || 'Failed to get job grid');
  }
};

/**
 * Reprocess a job with new parameters
 * @param {string} jobId - Job ID
 * @param {Object} processingParams - New processing parameters
 * @returns {Promise<Object>} Response
 */
export const reprocessJob = async (jobId, processingParams = {}) => {
  try {
    const response = await apiClient.post(`/photo_processing/jobs/${jobId}/reprocess/`, {
      processing_params: processingParams
    });
    return response.data;
  } catch (error) {
    console.error('Error reprocessing job:', error);
    throw new Error(error.response?.data?.error || 'Failed to reprocess job');
  }
};

/**
 * Get processing statistics
 * @returns {Promise<Object>} Processing statistics
 */
export const getProcessingStats = async () => {
  try {
    const response = await apiClient.get('/photo_processing/jobs/stats/');
    return response.data;
  } catch (error) {
    console.error('Error getting processing stats:', error);
    throw new Error(error.response?.data?.error || 'Failed to get processing stats');
  }
};

/**
 * Health check
 * @returns {Promise<Object>} Health status
 */
export const healthCheck = async () => {
  try {
    const response = await apiClient.get('/photo_processing/health/');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw new Error(error.response?.data?.error || 'Health check failed');
  }
};

/**
 * Download processed image
 * @param {string} jobId - Job ID
 * @returns {Promise<Blob>} Image blob
 */
export const downloadProcessedImage = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/download_processed/`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading processed image:', error);
    throw new Error(error.response?.data?.error || 'Failed to download processed image');
  }
};

/**
 * Download the original image of a job and trigger a browser save-as.
 * @param {string} jobId - Job ID
 * @param {string} [filename] - Filename to save as; falls back to a generic name
 * @returns {Promise<void>}
 */
export const downloadOriginalImage = async (jobId, filename) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/download-original/`, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `original_${jobId}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading original image:', error);
    throw new Error(error.response?.data?.error || 'Failed to download original image');
  }
};

/**
 * Download the original images of several jobs as ONE uncompressed ZIP and
 * trigger a single browser save-as.
 * @param {Array<number>} jobIds - Job IDs
 * @returns {Promise<{included: (number|null), skipped: (number|null)}>} Counts
 *   from the X-Included-Count / X-Skipped-Count response headers; null when the
 *   browser doesn't expose them (e.g. cross-origin dev setup).
 */
export const downloadOriginalsZip = async (jobIds) => {
  try {
    const response = await apiClient.post(
      '/photo_processing/jobs/batch-download-originals/',
      { job_ids: jobIds },
      {
        responseType: 'blob',
        // Building/downloading a large archive can exceed the default 30s
        timeout: 10 * 60 * 1000
      }
    );

    const pad = (n) => String(n).padStart(2, '0');
    const now = new Date();
    const timestamp =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_` +
      `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `originals_${timestamp}.zip`;
    document.body.appendChild(link);
    link.click();

    // Revoke later so a large blob isn't released before the download starts
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    const parseCount = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    return {
      included: parseCount(response.headers?.['x-included-count']),
      skipped: parseCount(response.headers?.['x-skipped-count'])
    };
  } catch (error) {
    console.error('Error downloading originals ZIP:', error);

    // With responseType 'blob' the JSON error body arrives as a Blob too
    let message = null;
    const errorData = error.response?.data;
    if (errorData instanceof Blob) {
      try {
        message = JSON.parse(await errorData.text())?.error || null;
      } catch (parseError) {
        message = null;
      }
    } else {
      message = errorData?.error || null;
    }

    throw new Error(message || 'Failed to download originals');
  }
};

/**
 * Get model information
 * @returns {Promise<Object>} Model metadata
 */
export const getModelInfo = async () => {
  try {
    const response = await apiClient.get('/photo_processing/model-info/');
    return response.data;
  } catch (error) {
    console.error('Error getting model info:', error);
    throw new Error(error.response?.data?.error || 'Failed to get model info');
  }
};

/**
 * Save user adjustments (cell rectangles and colors)
 * @param {string} jobId - Job ID
 * @param {Array} cellRects - Array of cell rectangles
 * @param {Array} colors - Array of extracted colors
 * @param {string} colorMode - Color extraction mode ('mean' or 'median')
 * @returns {Promise<Object>} Response
 */
export const saveUserAdjustments = async (jobId, gridStructure, gridDimensions, colors, colorMode, imageRotation) => {
  try {

    const response = await apiClient.post(`/photo_processing/jobs/${jobId}/save-adjustments/`, {
      adjusted_grid_structure: gridStructure,
      adjusted_grid_dimensions: gridDimensions,
      colors: colors,
      color_mode: colorMode,
      additional_rotation: imageRotation
    }, {
    headers: {
        'Content-Type': 'application/json'
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving user adjustments:', error);
    throw new Error(error.response?.data?.error || 'Failed to save user adjustments');
  }
};

/**
 * Get user adjustments
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} User adjustments data
 */
export const getUserAdjustments = async (jobId) => {
  try {
    const response = await apiClient.get(`/photo_processing/jobs/${jobId}/get-adjustments/`);
    return response.data;
  } catch (error) {
    console.error('Error getting user adjustments:', error);
    throw new Error(error.response?.data?.error || 'Failed to get user adjustments');
  }
};

export const getJobsIds = async (pageNum) => {
    try {
        const idsResponse = await apiClient.get(`/photo_processing/jobs/ids/?page=${pageNum}`);
        const jobIds = idsResponse.data.results;
        const hasMore = idsResponse.data.has_more;
        return [jobIds, hasMore];
    } catch (error) {
        console.error('Error getting job ids: ', error);
        throw new Error(error.response?.data?.error || 'Failed to get job ids');
    }
};

/**
 * Поиск photo analysis jobs, доступных пользователю.
 *
 * @param {Object} params - параметры поиска
 * @param {string} params.q - строка поиска (filename, email, first_name, last_name)
 * @param {string} params.filter_type - 'all' | 'mine' | 'shared'
 * @param {string} params.group_id - UUID группы (фильтр по конкретной группе)
 * @param {number} params.page - номер страницы (пагинация)
 * @param {string} params.date_from - дата "с" (created_at)
 * @param {string} params.date_to - дата "по" (created_at)
 * @returns {Promise<Object>} - ответ с пагинацией и списком jobs
 */
export const searchJobs = async (params = {}) => {
  try {
    const response = await apiClient.get('/photo_processing/jobs/search/', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching jobs:', error);
    throw new Error(error.response?.data?.error || 'Failed to search jobs');
  }
};

export const getBatchStatus = async (jobIds) => {
  try {
    const response = await apiClient.post('/photo_processing/batch-status/', {
      job_ids: Array.from(jobIds) // Convert Set to Array
    });
    return response.data.results;
  } catch (error) {
    console.error('Error getting batch status:', error);
    throw error;
  }
};

export const deleteJobs = async (jobIds) => {
  try {
    const response = await apiClient.delete('/photo_processing/jobs/batch-delete/', {
      data: { job_ids: jobIds }
    });
    return response.data;
  } catch (error) {
    console.error('Delete jobs error:', error);
    throw error;
  }
};

/**
 * Reprocess multiple jobs with Triton in one request.
 * @param {Array<number|string>} jobIds - Job IDs to reprocess
 * @param {Object} processingParams - Global processing parameters applied to every job
 * @param {Object} [yoloModelTypes] - Optional {job_id: yolo_model_type} map to set the
 *   YOLO model independently per job, overriding processingParams.yolo_model_type for that job
 * @returns {Promise<Object>} { reprocessed_count, reprocessed_ids, skipped_ids }
 */
export const batchReprocessJobs = async (jobIds, processingParams = {}, yoloModelTypes = {}) => {
  try {
    const response = await apiClient.post('/photo_processing/jobs/batch-reprocess/', {
      job_ids: jobIds,
      processing_params: processingParams,
      yolo_model_types: yoloModelTypes
    });
    return response.data;
  } catch (error) {
    console.error('Error batch reprocessing jobs:', error);
    throw new Error(error.response?.data?.error || 'Failed to reprocess jobs');
  }
};

export const getExperiments = async (pageNum, pageSize) => {
  try {
    const query = pageSize
      ? `?page=${pageNum}&page_size=${pageSize}`
      : `?page=${pageNum}`;
    const response = await apiClient.get(`/analysis/experiments/get_experiments/${query}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch experiments');
  }
};

export const deleteExperiments = async (experimentIds) => {
  try {
    const response = await apiClient.delete('/analysis/experiments/batch-delete/', {
      data: { experiment_ids: experimentIds }
    });
    return response.data;
  } catch (error) {
    console.error('Delete experiments error:', error);
    throw error;
  }
};

export const getExperiment = async (experimentId) => {
    try {
        const response = await apiClient.get(`/analysis/experiments/${experimentId}/`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Failed to get experiment');
    }
};

export const createExperiment = async (experiment) => {
  try {
    const response = await apiClient.post('/analysis/experiments/', experiment);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create experiment');
  }
};


export const updateExperiment = async (experimentId, experimentData) => {
  try {
    const response = await apiClient.patch(`/analysis/experiments/${experimentId}/`, experimentData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update experiment');
  }
};

export const deleteExperiment = async (experimentId) => {
  try {
    await apiClient.delete(`/analysis/experiments/${experimentId}/`);
    return true;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete experiment');
  }
};
// Add these functions to your existing photoProcessingApi.js

/**
 * Share an experiment with a group
 * @param {string} experimentId - Experiment ID
 * @param {Object} shareData - { group_id, permission_level }
 * @returns {Promise<Object>} Share info
 */
export const shareExperiment = async (experimentId, shareData) => {
  try {
    const response = await apiClient.post(`/analysis/experiments/${experimentId}/share/`, shareData);
    return response.data;
  } catch (error) {
    console.error('Error sharing experiment:', error);
    throw new Error(error.response?.data?.error || 'Failed to share experiment');
  }
};

/**
 * Поиск экспериментов, доступных пользователю.
 *
 * @param {Object} params - параметры поиска
 * @param {string} params.q - строка поиска (name, email, first_name, last_name)
 * @param {string} params.filterType - 'all' | 'mine' | 'shared'
 * @param {string} params.groupId - UUID группы (фильтр по конкретной группе)
 * @param {number} params.page - номер страницы (пагинация)
 * @param {number} params.pageSize - количество элементов на странице
 * @returns {Promise<Object>} - ответ с пагинацией и списком экспериментов
 */
export const searchExperiments = async (params = {}) => {
  try {
    const response = await apiClient.get('/analysis/experiments/search/', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching experiments:', error);
    throw new Error(error.response?.data?.error || 'Failed to search experiments');
  }
};
/**
 * Remove group access from experiment
 * @param {string} experimentId - Experiment ID
 * @param {Object} unshareData - { group_id }
 * @returns {Promise<Object>} Response
 */
export const unshareExperiment = async (experimentId, unshareData) => {
  try {
    const response = await apiClient.post(`/analysis/experiments/${experimentId}/unshare/`, unshareData);
    return response.data;
  } catch (error) {
    console.error('Error removing share:', error);
    throw new Error(error.response?.data?.error || 'Failed to remove group access');
  }
};

export const getAnalysisSummaries = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/experiments/${experimentId}/analysis-summaries/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analysis summaries:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch analysis summaries');
  }
};

export const refreshAnalysisSummaries = async (experimentId) => {
  try {
    const response = await apiClient.post(`/analysis/experiments/${experimentId}/analysis-summaries/refresh/`);
    return response.data;
  } catch (error) {
    console.error('Error refreshing analysis summaries:', error);
    throw new Error(error.response?.data?.error || 'Failed to refresh analysis summaries');
  }
};

export const updateAnalysisSummaryDescription = async (experimentId, summaryId, description) => {
  try {
    const response = await apiClient.patch(`/analysis/experiments/${experimentId}/analysis-summaries/${summaryId}/`, {
      description: description
    });
    return response.data;
  } catch (error) {
    console.error('Error updating analysis summary description:', error);
    throw new Error(error.response?.data?.error || 'Failed to update analysis summary description');
  }
};

export const cleanupAnalysisSummaries = async (experimentId) => {
  try {
    const response = await apiClient.post(`/analysis/experiments/${experimentId}/analysis-summaries/cleanup/`);
    return response.data;
  } catch (error) {
    console.error('Error cleaning up analysis summaries:', error);
    throw new Error(error.response?.data?.error || 'Failed to clean up analysis summaries');
  }
};

export const getAnalysisSummariesStatus = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/experiments/${experimentId}/analysis-summaries/status/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analysis summaries status:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch analysis summaries status');
  }
};

export const waitForSummaryUpdate = async (experimentId, initialVersion, maxWaitSeconds = 30) => {
  const startTime = Date.now();
  const maxWait = maxWaitSeconds * 1000;
  const checkInterval = 1000; // Check every second

  while (Date.now() - startTime < maxWait) {
    try {
      const status = await getAnalysisSummariesStatus(experimentId);

      if (status.success && status.version > initialVersion) {
        console.log(`Summary version updated from ${initialVersion} to ${status.version}`);
        return true;
      }

      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    } catch (error) {
      console.error('Error checking summary status:', error);
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
  }

  console.warn(`Timeout waiting for summary update after ${maxWaitSeconds} seconds`);
  return false;
};

/**
 * Get experiment sharing information
 * @param {string} experimentId - Experiment ID
 * @returns {Promise<Object>} Share info
 */
export const getExperimentShareInfo = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/experiments/${experimentId}/share_info/`);
    return response.data;
  } catch (error) {
    console.error('Error getting share info:', error);
    throw new Error(error.response?.data?.error || 'Failed to get sharing information');
  }
};

// Experiment Data API functions
export const getExperimentData = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/experiments/${experimentId}/data/`);
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

export const updateExperimentData = async (experimentId, dataUpdates) => {
  try {
    const response = await apiClient.post(`/analysis/experiments/${experimentId}/update_data/`, dataUpdates);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update experiment data');
  }
};

export const generateExperimentData = async (experimentId, config) => {
  try {
    const response = await apiClient.post(`analysis/experiments/${experimentId}/generate_experiment_data/`, config);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to generate experiment data');
  }
};

// Export functions
export const exportExperimentData = async (experimentId, format = 'excel') => {
  try {
    let endpoint;
    switch (format) {
      case 'excel':
        endpoint = `/analysis/experiments/${experimentId}/export_excel/`;
        break;
      case 'csv':
        endpoint = `/analysis/experiments/${experimentId}/export_csv/`;
        break;
      default:
        throw new Error('Unsupported export format');
    }

    const response = await apiClient.post(endpoint);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || `Failed to export data as ${format}`);
  }
};

export const uploadExternalFile = async (experimentId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/analysis/imported_experiment_files/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

export const getImportedFiles = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/imported_experiment_files/get_imported_files/`);
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

export const getImportedFileDetail = async (experimentId, fileId) => {
  try {
    const response = await apiClient.get(`/analysis/imported_experiment_files/${fileId}/`);
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

// api/photoProcessingApi.js
export const startImport = async (experimentId, fileId, selectedSheet = null, columnMapping = null) => {
  try {
    const data = { file_id: fileId };
    if (selectedSheet) {
      data.selected_sheet = selectedSheet;
    }
    if (columnMapping) {
      data.column_mapping = columnMapping;
    }

    const response = await apiClient.post(
      `/analysis/experiments/${experimentId}/start_import/`,
      data
    );
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

export const deleteImportedFile = async (experimentId, fileId) => {
  try {
    const response = await apiClient.delete(
      `/analysis/experiments/${experimentId}/imported_files/${fileId}/`
    );
    return response.data;
  } catch (error) {
    error.status = error.response?.status;
    throw error;
  }
};

export const createPlateSchema = async (schemaData) => {
  try {
    const response = await apiClient.post('/analysis/plate_schemas/', schemaData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create plate schema');
  }
};

export const updatePlateSchema = async (schemaId, schemaData) => {
    try {
        const response = await apiClient.patch(`/analysis/plate_schemas/${schemaId}/`, schemaData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Failed to update plate schema');
    }
};

export const getPlateSchema = async (schemaId) => {
    try {
        const response = await apiClient.get(`/analysis/plate_schemas/${schemaId}/`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.error || 'Failed to get plate schema');
    }
}

// Analysis API functions
export const getAnalyses = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/analyses/?experiment=${experimentId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch analyses');
  }
};

export const getAnalysesWithVisualizations = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/analyses/with-visualizations/`, {
      params: { experiment: experimentId }
    });

    if (response.data.success) {
      return response.data.results;
    } else {
      throw new Error(response.data.error || 'Failed to fetch analyses with visualizations');
    }
  } catch (error) {
    console.error('Error fetching analyses with visualizations:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch analyses with visualizations');
  }
};

// Light snapshot (ids, statuses, error-message checksums) of the analyses, visualizations and
// inferences of an experiment, without results: polled instead of with-visualizations
export const getAnalysesStatuses = async (experimentId) => {
  const response = await apiClient.get(`/analysis/analyses/statuses/`, {
    params: { experiment: experimentId }
  });
  return response.data.analyses;
};

export const getAnalysesBatchStatus = async (experimentId) => {
  try {
    const response = await apiClient.get(`/analysis/analyses/batch-status/`, {
      params: { experiment: experimentId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch batch status');
  }
};

export const createAnalysis = async (experimentId, analysisConfig) => {
  try {
    const payload = {
      experiment: experimentId,
      ...analysisConfig
    };

    const response = await apiClient.post('/analysis/analyses/', payload);
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    throw new Error(data?.error || data?.parameters || 'Failed to create analysis');
  }
};

/**
 * Estimate of a request to create analyses, before it is sent: number of analyses,
 * model fits and approximate time (nothing is created). The payload is the same as
 * for createAnalysis. An AbortSignal cancels a request that is no longer needed.
 */
export const estimateAnalysis = async (experimentId, analysisConfig, signal) => {
  const response = await apiClient.post(
    '/analysis/analyses/estimate/',
    { experiment: experimentId, ...analysisConfig },
    { signal }
  );
  return response.data;
};

/** Estimated time of the permutation test of a nested analysis ({nested: false} for other analyses) */
export const estimatePermutation = async (analysisId, nPermutations, signal) => {
  const response = await apiClient.get(`/analysis/analyses/${analysisId}/estimate-permutation/`, {
    params: { n_permutations: nPermutations },
    signal
  });
  return response.data;
};

export const getAnalysis = async (analysisId) => {
  try {
    const response = await apiClient.get(`/analysis/analyses/${analysisId}/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch analysis');
  }
};

export const updateAnalysis = async (analysisId, updates) => {
  try {
    const response = await apiClient.patch(`/analysis/analyses/${analysisId}/`, updates);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update analysis');
  }
};

export const deleteAnalysis = async (analysisId) => {
  try {
    await apiClient.delete(`/analysis/analyses/${analysisId}/`);
    return true;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete analysis');
  }
};

/**
 * Delete multiple analyses in a single request
 * @param {Array} analysisIds - Array of analysis UUIDs to delete
 * @returns {Promise<Object>} Response with deletion results
 */
export const batchDeleteAnalyses = async (analysisIds) => {
  try {
    const response = await apiClient.post('/analysis/analyses/batch-delete/', {
      analysis_ids: analysisIds
    });
    return response.data;
  } catch (error) {
    console.error('Batch delete analyses error:', error);
    throw new Error(error.response?.data?.error || 'Failed to delete analyses');
  }
};

export const rerunAnalysis = async (analysisId) => {
  try {
    const response = await apiClient.post(`/analysis/analyses/${analysisId}/rerun/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to rerun analysis');
  }
};

// Visualization API functions
export const getVisualizations = async (analysisId) => {
  try {
    const response = await apiClient.get(`/analysis/visualizations/?analysis=${analysisId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch visualizations');
  }
};

export const createVisualization = async (analysisId, visualizationConfig) => {
  try {
    const payload = {
      analysis: analysisId,
      ...visualizationConfig
    };

    const response = await apiClient.post('/analysis/visualizations/', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create visualization');
  }
};

export const getVisualization = async (visualizationId) => {
  try {
    const response = await apiClient.get(`/analysis/visualizations/${visualizationId}/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch visualization');
  }
};

export const deleteVisualization = async (visualizationId) => {
  try {
    await apiClient.delete(`/analysis/visualizations/${visualizationId}/`);
    return true;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete visualization');
  }
};

// Inference (prediction of unknown samples) API functions

// Flatten DRF error payload ({error} or {field: message | [messages]}) into one message
const getInferenceErrorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return fallback;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  const messages = Object.values(data)
    .map(value => (Array.isArray(value) ? value.join(' ') : String(value)))
    .filter(Boolean);
  return messages.length > 0 ? messages.join(' ') : fallback;
};

export const getAnalysisModelInfo = async (analysisId) => {
  try {
    const response = await apiClient.get(`/analysis/analyses/${analysisId}/model-info/`);
    return response.data;
  } catch (error) {
    throw new Error(getInferenceErrorMessage(error, 'Failed to fetch trained model info'));
  }
};

export const getAnalysisInferences = async (analysisId) => {
  try {
    const response = await apiClient.get(`/analysis/inferences/?analysis=${analysisId}`);
    return response.data;
  } catch (error) {
    throw new Error(getInferenceErrorMessage(error, 'Failed to fetch predictions'));
  }
};

export const createAnalysisInference = async (analysisId, inferenceConfig) => {
  try {
    const payload = {
      analysis: analysisId,
      ...inferenceConfig
    };

    const response = await apiClient.post('/analysis/inferences/', payload);
    return response.data;
  } catch (error) {
    throw new Error(getInferenceErrorMessage(error, 'Failed to start prediction'));
  }
};

export const deleteAnalysisInference = async (inferenceId) => {
  try {
    await apiClient.delete(`/analysis/inferences/${inferenceId}/`);
    return true;
  } catch (error) {
    throw new Error(getInferenceErrorMessage(error, 'Failed to delete prediction'));
  }
};

// src/api/photoProcessingApi.js

// Adjustments Tables API
export const createAdjustmentsTable = async (tableData) => {
  const response = await apiClient.post('/analysis/adjustments-tables/', tableData);
  return response.data;
};

export const getAdjustmentsTables = async (params = {}) => {
  const response = await apiClient.get('analysis/adjustments-tables/', { params });
  return response.data;
};

export const updateAdjustmentsTable = async (id, tableData) => {
  const response = await apiClient.put(`analysis/adjustments-tables/${id}/`, tableData);
  return response.data;
};

export const deleteAdjustmentsTable = async (id) => {
  const response = await apiClient.delete(`analysis/adjustments-tables/${id}/`);
  return response.data;
};

export const uploadExcelAdjustments = async (formData) => {
  const response = await apiClient.post('analysis/adjustments-tables/upload-excel/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getAdjustmentsTableStatus = async (tableId) => {
  const response = await apiClient.get(`analysis/adjustments-tables/${tableId}/upload-status/`);
  return response.data;
};

export const applyAdjustmentsToExperiment = async (experimentId, data) => {
  const response = await apiClient.post(`analysis/experiments/${experimentId}/apply-adjustments/`, data);
  return response.data;
};

export const getExperimentAdjustmentsStatus = async (experimentId) => {
  const response = await apiClient.get(`analysis/experiments/${experimentId}/get-adjustments-status/`);
  return response.data;
};

// File API functions
export const getExperimentFile = async (fileId) => {
  try {
    const response = await apiClient.get(`/analysis/files/${fileId}/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch files');
  }
};

function decodeQuotedPrintable(text) {
  return text
    .replace(/_/g, ' ')  // Q encoding uses _ for space
    .replace(/=([0-9A-F]{2})/g, (match, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
}

export const downloadFile = async (fileId) => {
  try {
    const response = await apiClient.get(`/analysis/files/${fileId}/download/`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Get filename from response headers - axios headers are lowercase
    const contentDisposition = response.headers['content-disposition'];
    console.log('Content-Disposition header:', contentDisposition);

    let filename = 'experiment_data.xlsx';

    // Extract filename from Content-Disposition header
    if (contentDisposition) {
      // First decode MIME encoded-word if present
      let decodedCD = contentDisposition;

      // Handle MIME encoded-word format (both B and Q encodings)
      if (contentDisposition.includes('=?utf-8?') || contentDisposition.includes('=?UTF-8?')) {
        try {
          // Match both B and Q encodings
          const mimeMatch = contentDisposition.match(/=\?utf-8\?([bq])\?(.+?)\?=/i);
          if (mimeMatch) {
            const encoding = mimeMatch[1].toLowerCase();
            const encodedText = mimeMatch[2];

            if (encoding === 'b') {
              // Base64 decoding
              decodedCD = atob(encodedText);
            } else if (encoding === 'q') {
              // Quoted-Printable decoding
              decodedCD = decodeQuotedPrintable(encodedText);
            }
            console.log('Decoded MIME encoded-word:', decodedCD);
          }
        } catch (e) {
          console.warn('Failed to decode MIME encoded-word:', e);
        }
      }

      // Now parse the decoded content disposition
      // Method 1: Try UTF-8 encoded filename first (filename*=UTF-8'')
      const utf8FilenameMatch = decodedCD.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8FilenameMatch && utf8FilenameMatch[1]) {
        try {
          filename = decodeURIComponent(utf8FilenameMatch[1]);
          console.log('Using UTF-8 filename:', filename);
        } catch (e) {
          console.warn('Failed to decode UTF-8 filename, trying alternative:', e);
        }
      }

      // Method 2: Try regular filename with quotes (filename="")
      if (!utf8FilenameMatch) {
        const filenameMatch = decodedCD.match(/filename="([^"]+)"/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
          console.log('Using quoted filename:', filename);
        }
        // Method 3: Try unquoted filename
        else {
          const filenameMatch = decodedCD.match(/filename=([^;]+)/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim();
            // Remove quotes if present
            filename = filename.replace(/^["']|["']$/g, '');
            console.log('Using unquoted filename:', filename);
          }
        }
      }
    }

    // If filename contains percent-encoded Cyrillic characters, decode them
    try {
      if (filename.includes('%')) {
        const decoded = decodeURIComponent(filename);
        if (decoded !== filename) {
          filename = decoded;
          console.log('Decoded percent-encoded filename:', filename);
        }
      }
    } catch (e) {
      console.warn('Failed to decode percent-encoded filename:', e);
    }

    // Generate timestamp for filename
    const now = new Date();
    const timestamp = now.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    .replace(' ', '_')
    .replace(/[:]/g, '-');

    // Process the filename to insert timestamp
    if (filename.toLowerCase().endsWith('.xlsx')) {
      // Remove .xlsx extension, add timestamp, then add extension back
      const baseName = filename.slice(0, -5);
      filename = `${baseName}_${timestamp}.xlsx`;
    } else if (filename.toLowerCase().endsWith('.csv')) {
      const baseName = filename.slice(0, -4);
      filename = `${baseName}_${timestamp}.csv`;
    } else if (filename.toLowerCase().endsWith('.json')) {
      const baseName = filename.slice(0, -5);
      filename = `${baseName}_${timestamp}.json`;
    } else {
      // If no proper extension, just append timestamp
      filename = `${filename}_${timestamp}`;
    }

    console.log('Final filename with timestamp:', filename);

    // Set the download attribute with the extracted filename
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw new Error(error.response?.data?.error || 'Failed to download file');
  }
};

// Task status checking
export const getTaskStatus = async (taskId) => {
  try {
    const response = await apiClient.get(`/analysis/tasks/${taskId}/status/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get task status');
  }
};

// Polling function for long-running tasks
export const pollTaskStatus = async (taskId, onUpdate, maxAttempts = 60, interval = 2000) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const status = await getTaskStatus(taskId);

        if (onUpdate) {
          onUpdate(status);
        }

        if (status.state === 'SUCCESS') {
          resolve(status.result);
        } else if (status.state === 'FAILURE') {
          reject(new Error(status.error || 'Task failed'));
        } else if (attempts >= maxAttempts) {
          reject(new Error('Task timeout'));
        } else {
          // Continue polling
          setTimeout(poll, interval);
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          reject(error);
        } else {
          // Retry on error
          setTimeout(poll, interval);
        }
      }
    };

    poll();
  });
};

// Utility functions
export const validateDataRange = (range, maxRows, maxCols) => {
  const rangePattern = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/;
  const match = range.match(rangePattern);

  if (!match) {
    return { valid: false, error: 'Invalid range format. Use format like A1:Z100' };
  }

  const startCol = match[1].charCodeAt(0) - 65;
  const startRow = parseInt(match[2]) - 1;
  const endCol = match[3].charCodeAt(0) - 65;
  const endRow = parseInt(match[4]) - 1;

  if (startCol < 0 || endCol >= maxCols || startRow < 0 || endRow >= maxRows) {
    return { valid: false, error: 'Range exceeds table dimensions' };
  }

  if (startCol > endCol || startRow > endRow) {
    return { valid: false, error: 'Invalid range: start must be before end' };
  }

  return {
    valid: true,
    startCol,
    startRow,
    endCol,
    endRow,
    rows: endRow - startRow + 1,
    cols: endCol - startCol + 1
  };
};

export const formatAnalysisResults = (analysis) => {
  if (!analysis.results) return null;

  const results = analysis.results;

  switch (analysis.analysis_type) {
    case 'pca':
      return {
        type: 'PCA',
        components: results.n_components,
        explainedVariance: results.explained_variance_ratio,
        cumulativeVariance: results.cumulative_variance,
        hasScores: !!results.transformed_data
      };

    case 'lda':
      return {
        type: 'LDA',
        accuracy: results.cv_mean?.toFixed(3),
        accuracyStd: results.cv_std?.toFixed(3),
        classes: results.classes?.length || 0,
        hasScores: !!results.transformed_data,
        hasConfusionMatrix: !!results.confusion_matrix
      };

    case 'logistic':
      return {
        type: 'Logistic Regression',
        accuracy: results.cv_mean?.toFixed(3),
        accuracyStd: results.cv_std?.toFixed(3),
        classes: results.classes?.length || 0,
        hasProbabilities: !!results.prediction_probabilities,
        hasCoefficients: !!results.coefficients
      };

    case 'svm':
      return {
        type: 'SVM',
        accuracy: results.cv_mean?.toFixed(3),
        accuracyStd: results.cv_std?.toFixed(3),
        classes: results.classes?.length || 0,
        hasProbabilities: !!results.prediction_probabilities
      };

    case 'random_forest':
      return {
        type: 'Random Forest',
        accuracy: results.cv_mean?.toFixed(3),
        accuracyStd: results.cv_std?.toFixed(3),
        classes: results.classes?.length || 0,
        hasFeatureImportance: !!results.feature_importances
      };

    default:
      return {
        type: analysis.analysis_type.toUpperCase(),
        hasResults: true
      };
  }
};

// Error handling helper
export const handleApiError = (error) => {
  console.error('API Error:', error);

  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const data = error.response.data;

    if (status === 400) {
      return data.error || 'Invalid request data';
    } else if (status === 401) {
      return 'Authentication required';
    } else if (status === 403) {
      return 'Permission denied';
    } else if (status === 404) {
      return 'Resource not found';
    } else if (status === 500) {
      return 'Server error. Please try again later.';
    }

    return data.error || `Server error (${status})`;
  } else if (error.request) {
    // Network error
    return 'Network error. Please check your connection.';
  } else {
    // Other error
    return error.message || 'An unexpected error occurred';
  }
};

// Export all functions
export default {
  API_URL,
  apiClient,
  uploadImages,
  processImages,
  getJobStatus,
  getJobResults,
  sharePhotoJob,
  unsharePhotoJob,
  getPhotoJobShareInfo,
  getJobGrid,
  reprocessJob,
  batchReprocessJobs,
  getProcessingStats,
  healthCheck,
  downloadProcessedImage,
  downloadOriginalImage,
  downloadOriginalsZip,
  getModelInfo,
  saveUserAdjustments,
  getUserAdjustments,
  getJobsIds,
  searchJobs,
  getBatchStatus,
  deleteJobs,
  getExperiments,
  deleteExperiments,
  getExperiment,
  createExperiment,
  updateExperiment,
  deleteExperiment,
  shareExperiment,
  unshareExperiment,
  getExperimentShareInfo,
  updateAnalysisSummaryDescription,
  refreshAnalysisSummaries,
  searchExperiments,

  // Experiment Data
  getExperimentData,
  updateExperimentData,
  generateExperimentData,
  exportExperimentData,

  // Analysis
  getAnalyses,
  getAnalysesWithVisualizations,
  getAnalysesStatuses,
  getAnalysesBatchStatus,
  createAnalysis,
  getAnalysis,
  updateAnalysis,
  deleteAnalysis,
  rerunAnalysis,
  batchDeleteAnalyses,

  // Visualizations
  getVisualizations,
  createVisualization,
  getVisualization,
  deleteVisualization,

  //AdjustmentsTable
  getAdjustmentsTables,
  createAdjustmentsTable,
  updateAdjustmentsTable,
  deleteAdjustmentsTable,
  uploadExcelAdjustments,
  getExperimentAdjustmentsStatus,
  getAdjustmentsTableStatus,

  // Files
  getExperimentFile,
  downloadFile,

  // Tasks
  getTaskStatus,
  pollTaskStatus,

  // Utilities
  validateDataRange,
  formatAnalysisResults,
  handleApiError
};